import List from '@pelckmans/elementis/components/list';
import ChapterIcon from '@pelckmans/elementis/icons/chapter';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import ModuleContext from '../../_contexts/ModuleContext';
import Pill from '@pelckmans/elementis/components/pill';
import Avatar from '@pelckmans/elementis/components/avatar';
import AvatarImage from '@pelckmans/elementis/components/avatar/avatar-image';
import { buildGroupProps } from '../../../../utils/elementisUtils';
import { getFullHierarchyDescriptionOfNode } from '../../../../utils/getNodeDescription';
import useLicenses from '../../../../hooks/useLicenses';
import stripHtml from '../../../../utils/wordlist/stripHtml';
import { SearchResultDigibookPage } from './../api/search-api';
import AccountContext from '../../../../contexts/AccountContext';
import { buildCodexUrl } from '../../utils/digibookAppUtils';
import { ANALYTICS_EVENT_SUBLOCATIONS, ANALYTICS_EVENT_TYPES } from '../../../../constants/analyticsEvents';
import useAnalytics from '../../../../hooks/useAnalytics';

interface Props {
  searchItem: SearchResultDigibookPage;
}

function SearchResultDigibookPageItem({ searchItem }: Props) {
  const { module, allModules } = useContext(ModuleContext);
  const { t } = useTranslation();
  const { isModuleLicensed } = useLicenses();
  const { account } = useContext(AccountContext);
  const analytics = useAnalytics();

  const targetModule = allModules.find(m => m.id === searchItem.moduleId);

  if (!module || !targetModule) return null;
  const licensedModule = isModuleLicensed(targetModule);

  const content = searchItem.name ? searchItem.name : undefined;
  const name = searchItem.metadata.digibookName;
  const digibookId = searchItem.metadata.digibookId;

  const captureOpenDigibookPage = () => {
    if (!digibookId) return;

    analytics.capture({
      eventType: ANALYTICS_EVENT_TYPES.DIGIBOOK_ACCESSED,
      objectId: digibookId,
      subLocationId: ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_SEARCH,
    });
  };

  return (
    <List.Item
      key={searchItem.id}
      as="link"
      title={!licensedModule ? stripHtml(name) : name}
      subText={content}
      disabled={!licensedModule}
      target="_blank"
      rel="noopener noreferrer"
      href={buildCodexUrl({
        digibookId: digibookId,
        superModuleId: module.id,
        accountProvider: account?.provider,
        page: searchItem.pageNumber,
      })}
      onClick={captureOpenDigibookPage}
      pillGroupProps={buildGroupProps(
        <>
          <Pill key="scope-pill">{t('PAGES.SEARCH.RESULTS.DIGIBOOK_PAGE')}</Pill>
          <Pill
            key="module-pill"
            size="sm"
            avatar={
              <Avatar>
                <AvatarImage size="xs" name={targetModule!.niceName} src={targetModule!.styling?.logo || ''} />
              </Avatar>
            }
          >
            {targetModule?.niceName}
          </Pill>
          <Pill key="page-number-pill">
            {t('PAGES.SEARCH.RESULTS.PAGE_NUMBER', { pageNumber: searchItem.pageNumber })}
          </Pill>
          {searchItem.hierarchy && searchItem.hierarchy.length > 0 && (
            <Pill key="hierarchy-pill" icon={<ChapterIcon />}>
              {getFullHierarchyDescriptionOfNode(searchItem.hierarchy, t)}
            </Pill>
          )}
        </>,
      )}
    />
  );
}

export default SearchResultDigibookPageItem;
